import { useEffect, useId, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import {
  api,
  Cliente,
  CodigoAgrupador,
  CuentaDeCatalogo,
  ErrorDeApi,
  Naturaleza,
  Plantilla,
  ResultadoImportacion,
  RolDeUsuario,
  ValidacionCt,
} from '../servicios/api'
import { telon } from '../ganchos/telon'
import { useAvisos } from '../componentes/Toast'
import { AyudaDeCampo } from '../ayuda/AyudaDeCampo'

const NOMBRES_DE_PLANTILLA: Record<Plantilla, string> = {
  PersonaMoralRegimenGeneral: 'Persona moral · Régimen general (601)',
  PersonaFisicaActividadEmpresarial: 'Persona física · Actividad empresarial y profesional (612)',
  Arrendamiento: 'Persona física · Arrendamiento (606)',
  Resico: 'Régimen Simplificado de Confianza · RESICO (626)',
}

const PLANTILLA_POR_REGIMEN: Record<string, Plantilla> = {
  '601': 'PersonaMoralRegimenGeneral',
  '612': 'PersonaFisicaActividadEmpresarial',
  '606': 'Arrendamiento',
  '626': 'Resico',
}

function etiquetaDeNivel(cuenta: CuentaDeCatalogo): string {
  if (cuenta.esDeRegistro) return `${cuenta.nivel} · Registro`
  return cuenta.nivel === 1 ? '1 · Mayor' : `${cuenta.nivel} · Subcuenta`
}

const MESES_CORTOS = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
]

const MESES_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/** K5: el árbol se lee por peso y fondo, no por píxeles de sangría. */
function claseDeFila(cuenta: CuentaDeCatalogo): string {
  const sinAgrupador = cuenta.esDeRegistro && !cuenta.codigoAgrupadorSat ? ' sin-agrupador' : ''
  if (cuenta.esDeRegistro) return `cuenta-registro${sinAgrupador}`
  return cuenta.nivel === 1 ? 'cuenta-mayor' : 'cuenta-subcuenta'
}

/**
 * K3: el catálogo c_CodAgrup del SAT trae más de mil códigos, y un `select` con todos no es
 * una ayuda sino otra búsqueda. Se ofrecen los de la misma raíz que el número de cuenta
 * (601-004 → 601.*) y con la misma naturaleza; para el resto queda «Editar», que trae el
 * `datalist` completo.
 */
export function agrupadoresSugeridos(
  agrupadores: CodigoAgrupador[],
  cuenta: CuentaDeCatalogo,
): CodigoAgrupador[] {
  const raiz = cuenta.numero.split('-')[0]
  return agrupadores.filter(
    (a) => a.naturaleza === cuenta.naturaleza && (a.codigo === raiz || a.codigo.startsWith(`${raiz}.`)),
  )
}

/** Catálogo de cuentas del cliente (RF-301, RF-302, RF-303 · RN-05 · cédula B). */
export function CatalogoDeCuentas() {
  const { id: clienteId } = useParams<{ id: string }>()
  const [cliente, setCliente] = useState<Cliente>()
  const [cuentas, setCuentas] = useState<CuentaDeCatalogo[]>()
  const [validacion, setValidacion] = useState<ValidacionCt>()
  const [agrupadores, setAgrupadores] = useState<CodigoAgrupador[]>([])
  const [error, setError] = useState('')
  const avisar = useAvisos()
  const [editando, setEditando] = useState<CuentaDeCatalogo | null>(null)
  const [creandoBajo, setCreandoBajo] = useState<CuentaDeCatalogo | null | 'mayor'>(null)
  const [mostrarTodas, setMostrarTodas] = useState(false)
  const [importando, setImportando] = useState(false)
  const [generandoCt, setGenerandoCt] = useState(false)
  // K2: el periodo que se mira. Arranca en el mes en curso; el catálogo se consulta tanto
  // para mantenerlo como para revisarlo contra un mes concreto.
  const [periodo] = useState(() => {
    const hoy = new Date()
    return { anio: hoy.getFullYear(), mes: hoy.getMonth() + 1 }
  })
  const [asignando, setAsignando] = useState<string | null>(null)
  const [reiniciando, setReiniciando] = useState(false)
  // Rol del usuario: `undefined` mientras se pregunta, `null` si no se pudo saber. Es presentación,
  // como en las demás pantallas: sin saberlo se muestra el botón y el servidor decide (403).
  const [rol, setRol] = useState<RolDeUsuario | null>()
  const esAdministrador = rol !== undefined && (rol === null || rol === 'Administrador')

  async function recargar() {
    if (!clienteId) return
    try {
      const [datosCliente, catalogo, ct] = await Promise.all([
        api.obtenerCliente(clienteId),
        api.catalogoDeCuentas(clienteId, periodo.anio, periodo.mes),
        api.validacionCt(clienteId),
      ])
      setCliente(datosCliente)
      setCuentas(catalogo)
      setValidacion(ct)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el catálogo.')
    }
  }

  useEffect(() => {
    void recargar()
    api.codigosAgrupadores().then(setAgrupadores).catch(() => setAgrupadores([]))
    api.despachoActual().then((d) => setRol(d.rolActual)).catch(() => setRol(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId])

  if (error && !cuentas) return <p className="error" role="alert">{error}</p>
  if (!clienteId || !cliente || !cuentas || !validacion) return <p>Cargando…</p>

  async function generarPlantilla(plantilla: Plantilla) {
    try {
      const r = await api.generarPlantilla(clienteId!, plantilla)
      avisar(`Catálogo generado: ${r.cuentasCreadas} cuentas. Ajusta lo que necesites.`)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo generar la plantilla.')
    }
  }

  async function desactivar(cuenta: CuentaDeCatalogo) {
    try {
      await api.desactivarCuenta(clienteId!, cuenta.id)
      avisar(`Cuenta ${cuenta.numero} desactivada (las cuentas nunca se eliminan).`)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo desactivar la cuenta.')
    }
  }

  // K3: reusa `editarCuenta` con el nombre y la naturaleza que ya tiene, así el aviso de
  // reenvío al SAT (RF-303) sale igual que desde el modal y no hay dos caminos que diverjan.
  async function asignarAgrupador(cuenta: CuentaDeCatalogo, codigo: string) {
    if (!codigo) return
    setAsignando(cuenta.id)
    setError('')
    try {
      const r = await api.editarCuenta(clienteId!, cuenta.id, {
        nombre: cuenta.nombre,
        naturaleza: cuenta.naturaleza,
        codigoAgrupadorSat: codigo,
      })
      avisar(
        r.procedeReenviarCatalogoSat
          ? `Cuenta ${cuenta.numero}: agrupador ${codigo}. Procede reenviar el catálogo al SAT (RF-303).`
          : `Cuenta ${cuenta.numero}: agrupador ${codigo}.`,
      )
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo asignar el código agrupador.')
    } finally {
      setAsignando(null)
    }
  }

  async function reactivar(cuenta: CuentaDeCatalogo) {
    await api.reactivarCuenta(clienteId!, cuenta.id)
    await recargar()
  }

  // ------------------------------------------------------ catálogo vacío: plantillas
  if (cuentas.length === 0) {
    const sugerida = PLANTILLA_POR_REGIMEN[cliente.regimenFiscal]
    const visibles = (Object.keys(NOMBRES_DE_PLANTILLA) as Plantilla[]).filter(
      (p) => mostrarTodas || !sugerida || p === sugerida,
    )
    return (
      <>
        <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link></p>
        <h1>Catálogo de cuentas</h1>
        <p className="sutil">
          Este cliente aún no tiene catálogo. Genera uno desde una plantilla basada en el código
          agrupador del SAT y ajústalo después: la plantilla es un punto de partida, no una camisa de fuerza.
        </p>
        {!sugerida && (
          <p className="aviso" role="status">
            El régimen {cliente.regimenFiscal} · {cliente.regimenDescripcion} no tiene una plantilla
            específica en el MVP: elige la más parecida a la operación del cliente.
          </p>
        )}
        {error && <p className="error" role="alert">{error}</p>}
        <div className="tarjetas" data-recorrido="catalogo" style={{ flexWrap: 'wrap' }}>
          {visibles.map((p) => (
            <div key={p} className="tarjeta" style={{ maxWidth: 340 }}>
              <h2>{p === sugerida ? `Sugerida para el régimen ${cliente.regimenFiscal}` : 'Plantilla'}</h2>
              <p style={{ minHeight: 40 }}>{NOMBRES_DE_PLANTILLA[p]}</p>
              <button className={p === sugerida ? 'primario' : ''} onClick={() => void generarPlantilla(p)}>
                Usar esta plantilla
              </button>
            </div>
          ))}
        </div>
        {sugerida && !mostrarTodas && (
          <p className="nota">
            ¿La operación real del cliente no corresponde a su régimen?{' '}
            <button className="enlace" onClick={() => setMostrarTodas(true)}>Ver las demás plantillas</button>
          </p>
        )}
        <p className="nota">
          ¿El cliente ya trae catálogo de otro sistema?{' '}
          <button className="enlace" onClick={() => setImportando(true)}>Importar desde Excel/CSV</button>
        </p>

        {importando && (
          <FormularioDeImportacion
            clienteId={clienteId}
            alCerrar={(mensaje) => {
              setImportando(false)
              if (mensaje) avisar(mensaje)
              void recargar()
            }}
          />
        )}
      </>
    )
  }

  async function generarCt() {
    setGenerandoCt(true)
    setError('')
    try {
      const hoy = new Date()
      await api.descargarCt(clienteId!, hoy.getFullYear(), hoy.getMonth() + 1)
      avisar('XML CT generado, validado contra el XSD oficial y descargado.')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo generar el CT.')
    } finally {
      setGenerandoCt(false)
    }
  }

  // ------------------------------------------------------------------ catálogo
  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link></p>
      <header className="encabezado" data-recorrido="catalogo">
        <h1>Catálogo de cuentas</h1>
        {/* K1 · Aquí vivían seis botones y solo uno era de esta pantalla: cuatro generaban
            archivos del Anexo 24 —que no son catálogo, son envíos— y se mudaron a Control de
            envíos, donde ya se les sigue la pista.

            El anexo pedía además subir aquí «Plantillas por régimen» e «Importar Excel/CSV».
            No caben: los dos endpoints responden CATALOGO_NO_VACIO en cuanto el cliente tiene
            una sola cuenta, así que en esta pantalla —que solo existe con catálogo— serían dos
            botones que fallan siempre. El prototipo es una maqueta sin backend; quedan donde
            ya estaban, en el estado vacío, que es donde funcionan. */}
        <span className="encabezado-acciones">
          <button className="primario" onClick={() => setCreandoBajo('mayor')}>Nueva cuenta</button>
          {/* ADR-018 · Rehacer desde la plantilla un catálogo que nunca se usó, que todavía no es
              contabilidad. El servidor rechaza el reinicio si alguna cuenta tiene uso. */}
          {esAdministrador && (
            <button className="secundario" onClick={() => setReiniciando(true)}>Reiniciar catálogo</button>
          )}
        </span>
      </header>

      {error && <p className="error" role="alert">{error}</p>}

      {/* K4 · El bloqueo y la acción que desbloquea iban separados: el aviso abajo y el botón
          arriba en la cabecera, deshabilitado sin decir por qué salvo en un `title`. Van juntos:
          el banner explica la causa y lleva dentro el botón que no se puede pulsar todavía. */}
      <div className="aviso-critico" role={validacion.listoParaCt ? undefined : 'alert'}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {validacion.listoParaCt ? (
            <>
              <path d="M21.801 10A10 10 0 1 1 17 3.335" />
              <path d="m9 11 3 3L22 4" />
            </>
          ) : (
            <>
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </>
          )}
        </svg>
        <div>
          {validacion.listoParaCt ? (
            <>
              <strong>Todas las cuentas de registro tienen código agrupador del SAT.</strong>{' '}
              El XML de catálogo (CT) se puede generar.
            </>
          ) : (
            <>
              <strong>
                No se puede generar el XML de catálogo (CT): {validacion.cuentasSinAgrupador.length}{' '}
                {validacion.cuentasSinAgrupador.length === 1 ? 'cuenta de registro' : 'cuentas de registro'} sin
                código agrupador del SAT (RN-05).
              </strong>{' '}
              {validacion.cuentasSinAgrupador.map((c) => `${c.numero} · ${c.nombre}`).join(', ')}.
              {' '}Asígnalo en las filas marcadas.
            </>
          )}
        </div>
        <button
          className="primario"
          onClick={() => void generarCt()}
          disabled={!validacion.listoParaCt || generandoCt}
        >
          {generandoCt ? 'Generando…' : 'Generar XML CT'}
        </button>
      </div>

      <div className="tabla-envoltura">
      <table className="tabla tabla-catalogo">
        <thead>
          <tr>
            <th>Cuenta</th>
            <th>Nombre</th>
            <th>Nivel</th>
            <th>Naturaleza</th>
            <th>Código agrupador SAT</th>
            <th className="movimientos">Movimientos · {MESES_CORTOS[periodo.mes - 1]}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {cuentas.map((cuenta) => (
            <tr
              key={cuenta.id}
              className={claseDeFila(cuenta)}
              style={cuenta.estado === 'Desactivada' ? { opacity: 0.45 } : undefined}
            >
              {/* K5 · La sangría aritmética no distinguía mayor de subcuenta; ahora el nivel se
                  lee por peso y fondo, y la sangría solo separa las ramas profundas. */}
              <td className="numero-cuenta" style={{ paddingLeft: `${12 + (cuenta.nivel - 1) * 14}px` }}>
                {cuenta.numero}
              </td>
              <td>{cuenta.nombre}</td>
              <td>{etiquetaDeNivel(cuenta)}</td>
              <td>{cuenta.naturaleza}</td>
              <td>
                {!cuenta.esDeRegistro ? (
                  <span className="sutil">— acumulativa</span>
                ) : cuenta.codigoAgrupadorSat ? (
                  <span className="mono" title={cuenta.agrupadorNombre}>{cuenta.codigoAgrupadorSat}</span>
                ) : (
                  /* K3 · El agrupador que falta bloquea el CT del cliente entero. Obligar a abrir
                     «Editar» por cada cuenta convertía un desbloqueo de un clic en uno de cuatro. */
                  <span className="asignar-agrupador">
                    <span className="insignia urgente">Sin agrupador</span>
                    <select
                      aria-label={`Asignar código agrupador a ${cuenta.numero} ${cuenta.nombre}`}
                      value=""
                      disabled={asignando === cuenta.id}
                      onChange={(e) => void asignarAgrupador(cuenta, e.target.value)}
                    >
                      <option value="">{asignando === cuenta.id ? 'Asignando…' : 'Asignar…'}</option>
                      {agrupadoresSugeridos(agrupadores, cuenta).map((a) => (
                        <option key={a.codigo} value={a.codigo}>{`${a.codigo} · ${a.nombre}`}</option>
                      ))}
                    </select>
                  </span>
                )}
              </td>
              {/* K2 · Una acumulativa no lleva cero: cero se lee «no tuvo», y lo correcto es
                  «no puede tener». El backend ya distingue las dos cosas con null. */}
              <td className={cuenta.esDeRegistro ? 'movimientos' : 'movimientos no-acepta'}>
                {cuenta.esDeRegistro ? (cuenta.movimientosEnPeriodo ?? 0) : 'no acepta'}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {cuenta.estado === 'Activa' ? (
                  /* K6 · Tres acciones subrayadas en cada renglón de una tabla de doscientos
                     convierten la columna en ruido. «Editar» es la que se usa; las otras dos
                     quedan a un clic, no escondidas tras un hover que en táctil no existe. */
                  <>
                    <button className="enlace" onClick={() => setEditando(cuenta)}>Editar</button>
                    <details className="menu-fila">
                      <summary aria-label={`Más acciones de ${cuenta.numero}`}>···</summary>
                      <div className="menu-fila-opciones">
                        {cuenta.nivel < 5 && (
                          <button className="enlace" onClick={() => setCreandoBajo(cuenta)}>Subcuenta</button>
                        )}
                        <button className="enlace" onClick={() => void desactivar(cuenta)}>Desactivar</button>
                      </div>
                    </details>
                  </>
                ) : (
                  <button className="enlace" onClick={() => void reactivar(cuenta)}>Reactivar</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <p className="nota">
        Solo las cuentas de último nivel aceptan movimientos; la columna cuenta los de pólizas
        vigentes de {MESES_LARGOS[periodo.mes - 1]} {periodo.anio} —una póliza cancelada conserva
        sus movimientos, pero deja de contar—. No se pueden eliminar cuentas —solo desactivarse— y
        cambiar un código agrupador obliga a reenviar el catálogo al SAT (RF-303).
      </p>

      {creandoBajo !== null && (
        <FormularioDeCuenta
          clienteId={clienteId}
          padre={creandoBajo === 'mayor' ? null : creandoBajo}
          agrupadores={agrupadores}
          alCerrar={(mensaje) => {
            setCreandoBajo(null)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {reiniciando && (
        <ReinicioDeCatalogo
          clienteId={clienteId}
          cuentas={cuentas.length}
          alCerrar={(mensaje) => {
            setReiniciando(false)
            if (mensaje) {
              avisar(mensaje)
              void recargar()
            }
          }}
        />
      )}

      {editando && (
        <FormularioDeCuenta
          clienteId={clienteId}
          cuenta={editando}
          agrupadores={agrupadores}
          alCerrar={(mensaje) => {
            setEditando(null)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

    </>
  )
}

/**
 * Reiniciar el catálogo: borra todas las cuentas del cliente para generarlo de nuevo desde la plantilla.
 * Existe porque las plantillas anteriores al 20/10/2026 ligaban cuentas a códigos agrupadores equivocados.
 * Solo se puede si ninguna cuenta tiene pólizas, reglas, plantillas de póliza ni cuentas bancarias; si
 * no, el mensaje del servidor dice qué la usa y qué hacer, y se muestra tal cual.
 */
function ReinicioDeCatalogo({
  clienteId,
  cuentas,
  alCerrar,
}: {
  clienteId: string
  cuentas: number
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function reiniciar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (!motivo.trim()) return
    setError('')
    setEnviando(true)
    try {
      const r = await api.reiniciarCatalogo(clienteId, motivo.trim())
      alCerrar(`Se borraron ${r.cuentasBorradas} cuentas. Genera el catálogo de nuevo desde la plantilla.`)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo reiniciar el catálogo.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={reiniciar}
      >
        <h2 id={idTitulo}>Reiniciar el catálogo de cuentas</h2>
        <p>
          Se borran las {cuentas} cuentas del catálogo y después lo generas de nuevo desde la plantilla, que ya
          liga cada cuenta al código agrupador oficial del Anexo 24.
        </p>
        <p className="sutil">
          Solo se puede si ninguna cuenta tiene pólizas, reglas de contabilización, plantillas de póliza ni
          cuentas bancarias. Si alguna tiene uso, corrige su código agrupador desde «Editar». El reinicio queda
          en la bitácora con tu motivo y la lista de cuentas borradas.
        </p>

        <label>
          Motivo
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={500}
            required
          />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando || !motivo.trim()}>
            {enviando ? 'Reiniciando…' : 'Reiniciar catálogo'}
          </button>
        </div>
      </form>
    </div>
  )
}

/** Importación desde CSV con reporte de errores por renglón (RF-304, B-06). */
function FormularioDeImportacion({
  clienteId,
  alCerrar,
}: {
  clienteId: string
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [error, setError] = useState('')
  const [errores, setErrores] = useState<ResultadoImportacion['errores']>([])
  const [enviando, setEnviando] = useState(false)

  async function importar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const archivo = new FormData(evento.currentTarget).get('archivo') as File
    if (!archivo?.size) return
    setError('')
    setErrores([])
    setEnviando(true)

    try {
      const resultado = await api.importarCatalogo(clienteId, archivo)
      if (resultado.exito) {
        alCerrar(`Catálogo importado: ${resultado.cuentasCreadas} cuentas.`)
      } else {
        setErrores(resultado.errores)
      }
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo importar el archivo.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={importar}
      >
        <h2 id={idTitulo}>Importar catálogo desde Excel o CSV</h2>
        <p className="sutil">
          Columnas: <span className="mono">Numero, Nombre</span> (obligatorias) y{' '}
          <span className="mono">NumeroPadre, Naturaleza (D/A), CodigoAgrupador</span> (opcionales),
          en la primera hoja. Acepta archivos <span className="mono">.xlsx</span> o{' '}
          <span className="mono">.csv</span>. Nada se importa si hay errores.
        </p>

        <label>
          Archivo Excel (.xlsx) o CSV
          <input
            name="archivo"
            type="file"
            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            required
          />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        {errores.length > 0 && (
          <div className="error" role="alert" style={{ maxHeight: 220, overflow: 'auto' }}>
            <strong>{errores.length} {errores.length === 1 ? 'error' : 'errores'}; no se importó nada:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {errores.map((e, i) => (
                <li key={i}>Fila {e.fila}: {e.mensaje}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Importando…' : 'Importar'}
          </button>
        </div>
      </form>
    </div>
  )
}

/** Alta o edición de una cuenta; el agrupador se elige del catálogo c_CodAgrup (B-03). */
function FormularioDeCuenta({
  clienteId,
  cuenta,
  padre,
  agrupadores,
  alCerrar,
}: {
  clienteId: string
  cuenta?: CuentaDeCatalogo
  padre?: CuentaDeCatalogo | null
  agrupadores: CodigoAgrupador[]
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const esEdicion = Boolean(cuenta)

  async function guardar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const agrupador = String(datos.get('codigoAgrupadorSat') ?? '').trim()
    setError('')
    setEnviando(true)

    try {
      if (esEdicion) {
        const resultado = await api.editarCuenta(clienteId, cuenta!.id, {
          nombre: String(datos.get('nombre')),
          naturaleza: datos.get('naturaleza') as Naturaleza,
          codigoAgrupadorSat: agrupador || undefined,
        })
        alCerrar(
          resultado.procedeReenviarCatalogoSat
            ? 'Cuenta guardada. Cambió el código agrupador: procede reenviar el catálogo al SAT.'
            : 'Cuenta guardada.',
        )
      } else {
        await api.crearCuenta(clienteId, {
          numero: String(datos.get('numero')),
          nombre: String(datos.get('nombre')),
          cuentaPadreId: padre?.id,
          naturaleza: (datos.get('naturaleza') as Naturaleza) || undefined,
          codigoAgrupadorSat: agrupador || undefined,
        })
        alCerrar('Cuenta creada.')
      }
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo guardar la cuenta.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={guardar}
      >
        <h2 id={idTitulo}>
          {esEdicion
            ? `Editar cuenta ${cuenta!.numero}`
            : padre
              ? `Nueva subcuenta de ${padre.numero} · ${padre.nombre}`
              : 'Nueva cuenta de mayor'}
        </h2>

        {!esEdicion && (
          <label>
            Número de cuenta
            <input name="numero" required maxLength={30} placeholder={padre ? `${padre.numero}-001` : '100'} />
          </label>
        )}

        <label>
          Nombre
          <input name="nombre" required maxLength={200} defaultValue={cuenta?.nombre} />
        </label>

        <label>
          Naturaleza
          <select name="naturaleza" defaultValue={cuenta?.naturaleza ?? padre?.naturaleza ?? 'Deudora'}>
            <option value="Deudora">Deudora</option>
            <option value="Acreedora">Acreedora</option>
          </select>
        </label>

        <div className="con-ayuda">
          <label>
            Código agrupador SAT {esEdicion || padre ? '(obligatorio en cuentas de registro para el CT)' : '(opcional)'}
         
            <input
              name="codigoAgrupadorSat"
              list="lista-agrupadores"
              defaultValue={cuenta?.codigoAgrupadorSat}
              placeholder="601.56"
              maxLength={10}
            />
          </label>
          <AyudaDeCampo clave="codigo-agrupador" />
        </div>
        <datalist id="lista-agrupadores">
          {agrupadores.map((a) => (
            <option key={a.codigo} value={a.codigo}>{`${a.codigo} · ${a.nombre} (${a.naturaleza})`}</option>
          ))}
        </datalist>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  )
}
